import * as Api from '@ViewModels';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import {
	invalidateAllServiceJobFilters,
	invalidateAllServiceJobSummary,
	useGetExternalIntegrationsQuery,
	useServiceJobFilter,
	useServiceJobSummaryQuery,
} from '../../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { ToolsIcon } from '../../../svgs/icons/ToolsIcon';
import { DataBoardRow, DataBoardTable } from '../../DataBoardTable';
import { DeleteModal, DeleteType } from '../../DeleteModal';
import { EditServiceJobModal } from '../EditServiceJobModal';
import { SERVICE_JOB_PROVIDERS, formatServiceJobCurrency, formatServiceJobDate } from '../utils';

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const EntityServiceJobs = ({ entity }: { entity: Api.IEntity }) => {
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const hasServiceJobIntegration = externalIntegrations?.some(
		integration => integration.enabled && SERVICE_JOB_PROVIDERS.includes(integration.provider)
	);
	const canEdit = !hasServiceJobIntegration;

	const [jobToEdit, setJobToEdit] = React.useState<Partial<Api.IServiceJob> | null>(null);
	const [jobToDelete, setJobToDelete] = React.useState<Api.IServiceJob | null>(null);

	const filterRequest: Api.IServiceJobFilterRequest = {
		criteria: [
			{
				property: Api.ServiceJobFilterProperty.ContactId,
				value: entity.id ?? '',
			},
		],
	};

	const jobFilterQuery = useServiceJobFilter({ enabled: !!entity.id, filterRequest });
	const summaryQuery = useServiceJobSummaryQuery({ enabled: !!entity.id, filterRequest });

	const jobs = React.useMemo(() => {
		return jobFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [jobFilterQuery.data]);

	const onDeleted = () => {
		setJobToDelete(null);
		invalidateAllServiceJobFilters();
		invalidateAllServiceJobSummary();
	};

	const newJob: Partial<Api.IServiceJob> = {
		contact: entity as Api.IProjectedContact,
		contactId: entity.id,
	};

	const summary = summaryQuery.data?.totalJobs
		? `${pluralize(summaryQuery.data.totalJobs, 'job')} · ${summaryQuery.data.completedJobs} completed · ${formatServiceJobCurrency(summaryQuery.data.totalAmount)} total`
		: undefined;

	const rows: DataBoardRow[] = jobs.map(job => ({
		id: job.id,
		name: job.jobNumber,
		statusTitle: job.jobStatus?.name,
		status: job.jobStatus?.name ? (
			<Pill variant='label' color={job.completedOn ? 'green' : 'blue'} label={job.jobStatus.name} />
		) : (
			'-'
		),
		fields: [job.summary || '-', job.jobType || '-', formatServiceJobDate(job.completedOn)],
		value: formatServiceJobCurrency(job.total),
		actions:
			canEdit && job.canModify ? (
				<Dropdown>
					<DropdownEllipsisTrigger aria-label={`Actions for job ${job.jobNumber ?? ''}`.trim()} />
					<DropdownContent>
						<DropdownItem onClick={() => setJobToEdit(job)}>Edit</DropdownItem>
						<DropdownItem onClick={() => setJobToDelete(job)}>Delete</DropdownItem>
					</DropdownContent>
				</Dropdown>
			) : undefined,
	}));

	return (
		<div>
			<DataBoardTable
				icon={<ToolsIcon fill={THEME.colors.primary.contrastText} />}
				iconColor='blue'
				title='Service Jobs'
				summary={summary}
				addItemName='Service Job'
				addDisabledReason={
					canEdit ? undefined : 'Service jobs sync from your field service integration, so add them there.'
				}
				onAdd={() => setJobToEdit(newJob)}
				nameLabel='Job'
				statusLabel='Status'
				fieldLabels={['Summary', 'Type', 'Completed']}
				valueLabel='Total'
				rows={rows}
				isLoading={jobFilterQuery.isPending}
				emptyText={
					jobFilterQuery.isError
						? 'Couldn’t load this contact’s service jobs.'
						: 'No service jobs exist for this contact.'
				}
			>
				{jobFilterQuery.hasNextPage && !jobFilterQuery.isFetchingNextPage ? (
					<Waypoint bottomOffset='-200px' onEnter={() => jobFilterQuery.fetchNextPage()} />
				) : null}
			</DataBoardTable>

			{jobToEdit ? (
				<EditServiceJobModal
					key={jobToEdit.id ?? 'new'}
					initialJob={jobToEdit}
					isOpen={!!jobToEdit}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setJobToEdit(null);
						}
					}}
					onSave={() => setJobToEdit(null)}
					onCancel={() => setJobToEdit(null)}
				/>
			) : null}

			{jobToDelete ? (
				<DeleteModal
					type={DeleteType.ServiceJob}
					item={jobToDelete}
					isOpen={!!jobToDelete}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setJobToDelete(null);
						}
					}}
					onConfirm={onDeleted}
				/>
			) : null}
		</div>
	);
};
